/**
 * Selezione e inizializzazione del gateway dati.
 *
 * L'applicazione non sceglie mai il backend: chiede `getGateway()` e riceve
 * l'implementazione configurata. È il punto in cui, in futuro, si innesterebbe
 * un backend dedicato senza toccare i domini.
 */

import { env, isDemoMode } from '@/config/env';
import type { DataGateway } from './gateway';
import { createSupabaseGateway } from './supabase-gateway';

export * from './gateway';
export { requestDemoReset } from './demo-reset';

let gatewayPromise: Promise<DataGateway> | null = null;

/** Identificativo dell'utente impersonato in modalità dimostrativa. */
let demoUserId: string | null = null;

export function setDemoUserId(userId: string | null): void {
  demoUserId = userId;
}

export function getDemoUserId(): string | null {
  return demoUserId;
}

export type GatewayProgressListener = (message: string) => void;

let progressListener: GatewayProgressListener | null = null;

export function onGatewayProgress(listener: GatewayProgressListener | null): void {
  progressListener = listener;
}

export function getGateway(): Promise<DataGateway> {
  if (gatewayPromise) return gatewayPromise;

  gatewayPromise = (async () => {
    if (isDemoMode) {
      // Caricamento differito: il modulo PGlite pesa alcuni megabyte e non deve
      // entrare nel bundle di chi usa Supabase.
      const { createPgliteGateway } = await import('./pglite-gateway');
      return createPgliteGateway({
        getUserId: () => demoUserId,
        onProgress: (message) => progressListener?.(message),
      });
    }

    return createSupabaseGateway({
      url: env.VITE_SUPABASE_URL!,
      anonKey: env.VITE_SUPABASE_ANON_KEY!,
    });
  })();

  return gatewayPromise;
}

/** Reimposta il gateway. Usato dai test e dal cambio di modalità. */
export function resetGateway(): void {
  gatewayPromise = null;
  demoUserId = null;
}
